// unit: filter
import {
    CreateClass,
    deepMixin,
    removeRepeat,
    isArray,
    isObject,
    isString,
    isFunction
} from "../../../funcs";
import Backbone from "backbone";
import FilterQuery from "./FilterQuery";
import Condition from "./Condition";


let CONDITIONS_TYPES = [
    "equal",
    "composite",
    "date",
    "in",
    "contain"
];

/**
     * Вспомогательный класс для построения фильтров
     * @constructs Filter
     * @param {Array | FilterQuery}  query  Массив с условиями для фильтрации
     * @param {Object | Array}  conditions  Объект с параметрами для фильтрации
     * @param {...string} serverVersion необязательный аргумент, версия сервера
     */
let Filter = CreateClass("Filter");
Filter.version = "2.0";
Filter.serverVersion = "1.1";

let id = 0;
Filter.prototype.init = function(query, conditions, serverVersion) {
    this.id = ++id;

    if ( isObject(query) && query.tryNewFilterFormat ) {
        this.value = query.value;
        this.tryNewFilterFormat = true;
    }

    if ( arguments.length === 1 && isObject(query) ) {
        if (
            Object.keys(query).length == 2
                && isArray(query.query)
                && isObject(query.conditions)
        ) {
            conditions = query.conditions;
            query = query.query;
        } else {
            // в формах частенько хочется написать так:
            // is: {
            //    hasUser: {
            //       key: "ID_USER",
            //       type: "composite",
            //       withNulls: false
            //    }
            // }
            //
            // вместо
            // is: {
            //    hasUser: {
            //       userCondition: {
            //           key: "ID_USER",
            //           type: "composite",
            //           withNulls: false
            //       }
            //    }
            // }

            if (
                CONDITIONS_TYPES.indexOf(query.type) != -1
                    && isString(query.key)
            ) {
                let tmp = {};
                tmp[ query.key ] = query;
                query = tmp;
            }

            conditions = query;
            query = ["AND"].concat(Object.keys(conditions));
        }
    }

    if ( query instanceof FilterQuery ) {
        this.query = new FilterQuery();
    } else {
        this.query = new FilterQuery(query);
    }

    this.conditions = {};
    conditions = conditions || {};
    let condition;

    for (let key in conditions) {
        condition = conditions[key];

        if ( condition instanceof Condition ) {
            this.conditions[key] = condition;
        } else if ( Object.keys(condition).length ) {
            this.conditions[key] = new Condition(condition);
        }
    }

    this.serverVersion = serverVersion || Filter.serverVersion;
};

Filter.prototype.setQuery = function(query) {
    this.query = query;
};

/**
     * Преобразование текущего фильтра в серверную версию 2.0
     * @return {Object} смотри пример
     * @example <caption> server filter 2.0 </caption>
     * // фильтр строк, которые:
     * // в поле DT_CREATE имеют значение 1458658310960,
     * // в поле NAME содержат подстроку "вася" регистро независимо,
     * // И
     * // или в поле PHONE1 имеют значение "+7 999 999 99 99"
     * // или в поле PHONE2 имеют значение "+7 999 999 99 99"
     * {
     *    query: [ "AND", "x", "y", [ "OR", "a", "b" ] ],
     *    conditions: {
     *       "x": {
     *         type: "date",
     *         key: "DT_CREATE",
     *         value: 1458658310960
     *       },
     *       "y": {
     *         type: "contain",
     *         key: "NAME",
     *         value: "вася"
     *       },
     *       "a": {
     *         type: "equal",
     *         key: "PHONE1",
     *         value: "+7 999 999 99 99"
     *       },
     *       "b": {
     *         type: "equal",
     *         key: "PHONE2",
     *         value: "+7 999 999 99 99"
     *       }
     *    }
     * }
     *
     * // P.S. более подробный список доступных
     * // типов фильтрации @see Filter.Condition
     */
Filter.prototype["compile2.0"] = function() {
    let conditions = {};
    for (let key in this.conditions) {
        conditions[key] = this.conditions[key].compile2version("2.0");
    }

    return {
        query: this.query.compile(),
        conditions
    };
};

/**
     * Преобразование текущего фильтра в серверную версию 1.1
     * @return {Object} смотри пример
     * @deprecated
     */
Filter.prototype["compile1.1"] = function() {
    let filters = {};
    let condition;
    let key;

    for (let ckey in this.conditions) {
        condition = this.conditions[ckey];
        key = condition.key;
        filters[key] = condition.compile2version("1.1");
    }

    return filters;
};

Filter.prototype["compile2.9"] = function() {
    let filters = [];
    let condition;

    for (let ckey in this.conditions) {
        condition = this.conditions[ckey];

        if ( !condition.key ) {
            throw new Error ("Filter:CompileError: filter condition without key has not supported");
        }

        filters.push(condition.compile2version("2.9"));
    }

    let result = [];
    filters.forEach(function(filter, i) {
        result.push(filter);

        if ( i != filters.length - 1 ) {
            result.push("and");
        }
    }, this);

    result = result.length === 1 ? result[0] : result; ///Разворачивает лишние скобки
    return { value: result, tryNewFilterFormat: true };
};

/**
     * Преобразование к строке JSON
     * @return {String} скомпилированный фильтр в актуальную версию в виде JSON строки
     */
Filter.prototype.toJSON = function() {
    return JSON.stringify(this.compile());
};

/**
     * Преобразование в конкретную серверную версию
     * @throws {Error}
     * @param  {String} version серверная версия фильтра
     * @return {Object}
     */
Filter.prototype.compile2version = function(version) {
    if ( !isFunction(this["compile" + version]) ) {
        throw new Error("Filter:VersionError: version " + version + " has not supported");
    }

    return this["compile" + version]();
};

/**
     * Проверка возможности преобразования
     * @param  {String} version версия, которую мы хотим проверить
     * @param  {Object} params  параметры проверки, объект вида:
     * {
     *   log: {Boolean} // true, если необходимо вывести
     *                  // в консоль, почему нельзя преобразовать фильтр
     * }
     * @return {Boolean}         true, если преобразование возможно и пройдет без ошибок
     */
Filter.prototype.checkVersion = function(version, params) {
    params = deepMixin({
        log: false
    }, params);

    try {
        this.compile2version(version);
        return true;
    } catch (e) {
        if ( params.log ) {
            console.error(e);
        }

        return false;
    }
};

/**
     * Преобразуем текущих объект фильтра в объект, который распознает сервер,
     * учитывая serverVersion
     * @return {Object | Undefined} вид объекта зависит от версии сервера
     * этот метод вернет Undefined, если преобразовать фильтр нельзя
     */
Filter.prototype.compile = function() {
    let version = this.serverVersion;

    if ( !this.checkVersion(version, { log: true }) ) {
        return;
    }

    return this.compile2version(version);
};

/**
     * Проверяем, подходит ли модель под фильтр
     * @param  {Backbone.Model} model модель с данными
     * @return {Boolean}       true, если модель попадает под фильтр
     */
Filter.prototype.check = function(model) {
    if ( !(model instanceof Backbone.Model) ) {
        model = new Backbone.Model( model );
    }

    let condResults = {};
    let condition;

    for (let key in this.conditions) {
        condition = this.conditions[key];
        condResults[key] = condition.check(model);
    }

    return this.query.check(condResults);
};

/**
     * На 13.04.2016 фильтры использовались: 1 столбец, 1 фильтр.
     * Дополняем текущий фильтр, если уже существует фильтр по такому столбцу,
     * то он будет заменен
     * @param {Filter} filter фильтр, с которым требуется объединиться
     */
Filter.prototype.add = function(filter) {
    if ( !(filter instanceof Filter) ) {
        filter = new Filter(["AND"].concat(Object.keys(filter)), filter);
    }

    // разруливаем конфликты имен в 2х объектах conditions
    // ["AND", "x", "y"] {x: ..., y: ...}
    // ["AND", "x", "z"] {x: ..., z: ...}
    // result
    // ["AND", "x", "z"] {x1: ..., z: ...}
    let queries = filter.query.toArray();
    let condition;
    // let newKey;

    for (let key in filter.conditions) {
        condition = filter.conditions[key];
        let thisCondition = this.conditions[key];

        if ( !condition ) {
            console.error("bad condition");
        } else {
            if ( thisCondition ) {
                if ( !(thisCondition instanceof Condition) ) {
                    thisCondition = new Condition(thisCondition);
                }
            } else {
                thisCondition = new Condition();
            }

            thisCondition.merge(condition);
            this.conditions[key] = thisCondition;
        }

        // if ( this.conditions[key] ) {
        //     // одинаковые условия не дублируем
        //     if ( eqConds(this.conditions[key], condition) ) {
        //         continue;
        //     }

        //     newKey = key + "_" + filter.id;
        //     queries = replaceKeyInQuery(queries, key, newKey);
        //     this.conditions[ newKey ] = filter.conditions[ key ];
        // } else {
        //     this.conditions[ key ] = filter.conditions[ key ];
        // }

    }

    if ( this.query.operator == filter.query.operator ) {
        this.query.queries = this.query.queries.concat(queries.slice(1));
        // удаляем условия с одним и тем же именем на первом уровне
        // ["AND", "1", "1", ["OR", "1", "2"]] =>
        // ["AND", "1", ["OR", "1", "2"]]
        this.query.queries = removeRepeat(this.query.queries);
    } else {
        this.query.add(queries);
    }
};

/**
     * В курсоре объединяются 2 фильтра системный и обычный,
     * чтобы после объединения не было ошибок, клонируем фильтр
     * @return {Filter} копия фильтра
     */
Filter.prototype.clone = function() {
    let params = this["compile2.0"]();
    return new Filter(params.query, params.conditions);
};

/**
     * Удаляем часть фильтра из query и conditions
     * @param  {String | Array} keyOrKeys ключ(и) условия
     */
Filter.prototype.remove = function(keyOrKeys) {
    const keysArray = isArray(keyOrKeys)
        ? [...keyOrKeys]
        : [keyOrKeys];

    keysArray.forEach((key) => {
        delete this.conditions[key];
        this.query.remove(key);
    });
};

/**
     * Проверяем участвует ли столбец в фильтре
     * @param {String} key ключ столбца
     * @return {Boolean} true, если фильтр затрагивает столбец
     */
Filter.prototype.hasColumn = function(key) {
    for (let k in this.conditions) {
        if ( this.conditions[k].key == key ) {
            return true;
        }
    }
    return false;
};

Filter.prototype.getCondition = function(key) {
    let condition = this.conditions[key];

    if ( condition ) {
        return condition["compile2.0"]();
    }
};

/**
     * Добавляем или изменяем условие поиска
     * @param  {String} key       ключ условия (будет в query)
     * @param  {Condition | Object} condition объект условия
     */
Filter.prototype.setCondition = function(key, condition) {
    if ( !(condition instanceof Condition) ) {
        condition = new Condition(condition);
    }

    this.conditions[key] = condition;

    // бывают ситуации, где нужно добавить столбец
    if ( !this.query.has(key) ) {
        this.query.add(key);
    }
};

/**
     * удаляем условие по столбцу
     * @param  {String} key ключ условия
     */
Filter.prototype.removeCondition = function(key) {
    delete this.conditions[key];

    if ( this.query.has(key) ) {
        this.query.remove(key);
    }
};

/**
     * Пустой ли фильтр?
     * @return {Boolean} true, если filter.check(anyModel) === true
     */
Filter.prototype.isEmpty = function() {
    if ( this.query.isEmpty() ) {
        return true;
    }

    for (let key in this.conditions) {
        if ( !this.conditions[key].isEmpty() ) {
            return false;
        }
    }

    return true;
};

/**
     * Производим зачистку пустых условий для поиска по столбцам.
     * Пустое условие всегда возвращает все строки
     */
Filter.prototype.removeEmptyConditions = function() {
    let toRemove = [];

    for (let key in this.conditions) {
        if ( this.conditions[key].isEmpty() ) {
            toRemove.push(key);
        }
    }

    toRemove.forEach(this.removeCondition, this);
};

/**
     * Codition
     * @type {Class}
     * @memberof Filter
     */
Filter.Condition = Condition;
/**
     * Query
     * @type {Class}
     * @memberof Filter
     */
Filter.Query = FilterQuery;

export default Filter;
